/**
 * File: StructurizrEditor.tsx
 * Description: Split-pane editor for Structurizr DSL with Monaco syntax highlighting
 *              and Kroki preview with view switching
 * Author: System
 * Created: 2025-01-14
 * Last Modified: 2025-01-26
 */

import {
  CloudArrowUpIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import MonacoEditor, { type Monaco, type OnMount } from '@monaco-editor/react';
import React, { useState, useCallback, useEffect, useRef } from 'react';

import { useKrokiRenderer } from '../../hooks/useKrokiRenderer';

import {
  registerStructurizrLanguage,
  defineStructurizrTheme,
  registerStructurizrCompletionProvider,
  STRUCTURIZR_LANGUAGE_ID,
} from './structurizrLanguage';
import {
  ViewSelector,
  C4ViewType,
  detectAvailableViews,
  extractViewKey,
} from './ViewSelector';

interface StructurizrEditorProps {
  /**
   * Initial DSL content
   */
  initialContent?: string;
  /**
   * Callback when content changes
   */
  onChange?: (_content: string) => void;
  /**
   * Callback when content is saved
   */
  onSave?: (_content: string) => Promise<void>;
  /**
   * Height of the editor container
   */
  height?: string;
}

/**
 * StructurizrEditor component with split pane layout and Monaco editor
 * Left side: Monaco editor with DSL syntax highlighting
 * Right side: Kroki rendered diagram with view switching
 * @param props - Component props
 * @return JSX element for the editor
 */
export const StructurizrEditor: React.FC<StructurizrEditorProps> = ({
  initialContent = '',
  onChange,
  onSave,
  height = '600px',
}) => {
  const { renderDiagram, isLoading, error } = useKrokiRenderer();

  // Constants for magic numbers
  const INITIAL_LEFT_PANE_WIDTH = 50;
  const DEBOUNCE_DELAY_MS = 500;
  const MIN_PANE_WIDTH = 20;
  const MAX_PANE_WIDTH = 80;

  // State management
  const [dslContent, setDslContent] = useState<string>(initialContent);
  const [diagramUrl, setDiagramUrl] = useState<string>('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [selectedView, setSelectedView] = useState<C4ViewType>('systemContext');
  const [availableViews, setAvailableViews] = useState<C4ViewType[]>([
    'systemContext',
  ]);

  // Drag state for resizable panes
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [dragStart, setDragStart] = useState<number>(0);
  const [leftPaneWidth, setLeftPaneWidth] = useState<number>(
    INITIAL_LEFT_PANE_WIDTH,
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<Parameters<OnMount>[0] | null>(null);
  const monacoRef = useRef<Monaco | null>(null);

  /**
   * Handle Monaco editor mount
   * Registers Structurizr language and theme
   */
  const handleEditorDidMount: OnMount = (editorInstance, monaco) => {
    editorRef.current = editorInstance;
    monacoRef.current = monaco;

    // Register Structurizr DSL language
    registerStructurizrLanguage(monaco);
    defineStructurizrTheme(monaco);
    registerStructurizrCompletionProvider(monaco);

    // Set the custom theme
    monaco.editor.setTheme('structurizr-light');

    // Add keyboard shortcuts
    editorInstance.addCommand(
      monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS,
      () => {
        if (hasUnsavedChanges && !isSaving) {
          handleSave();
        }
      },
    );
  };

  /**
   * Update Kroki-rendered diagram image when DSL content or view changes
   * Debounced to avoid excessive API calls to Kroki renderer
   */
  useEffect(() => {
    const debounceTimer = setTimeout(async () => {
      if (dslContent.trim()) {
        // Get the view key if a specific view is selected
        const viewKey = extractViewKey(dslContent, selectedView);

        // Pass viewKey to Kroki for specific view rendering
        const imageUrl = await renderDiagram(dslContent, 'structurizr', viewKey);
        if (imageUrl) {
          // Revoke previous Kroki image blob URL to free browser memory
          if (diagramUrl) {
            URL.revokeObjectURL(diagramUrl);
          }
          setDiagramUrl(imageUrl);
        }

        // Detect available views from DSL
        const views = detectAvailableViews(dslContent);
        setAvailableViews(views);
      }
    }, DEBOUNCE_DELAY_MS);

    return () => clearTimeout(debounceTimer);
  }, [dslContent, selectedView, renderDiagram]);

  /**
   * Sync internal state when initialContent prop changes
   */
  useEffect(() => {
    if (initialContent !== dslContent) {
      setDslContent(initialContent);
      setHasUnsavedChanges(false);
    }
  }, [initialContent]);

  /**
   * Handle DSL content changes from Monaco editor
   */
  const handleContentChange = useCallback(
    (value: string | undefined) => {
      const newContent = value ?? '';
      setDslContent(newContent);
      setHasUnsavedChanges(true);
      onChange?.(newContent);
    },
    [onChange],
  );

  /**
   * Handle save action
   */
  const handleSave = useCallback(async () => {
    if (!onSave) {
      return;
    }

    try {
      setIsSaving(true);
      setSaveError(null);
      await onSave(dslContent);
      setHasUnsavedChanges(false);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to save diagram';
      setSaveError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  }, [dslContent, onSave]);

  /**
   * Handle mouse drag for pane resizing
   */
  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
      setIsDragging(true);
      setDragStart(e.clientX);
    },
    [],
  );

  /**
   * Handle mouse move during drag
   */
  useEffect(() => {
    if (!isDragging) {
      return undefined;
    }

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || !containerRef.current) {
        return;
      }

      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();
      const deltaX = e.clientX - dragStart;
      const containerWidth = containerRect.width;

      const newLeftWidth = Math.max(
        MIN_PANE_WIDTH,
        Math.min(
          MAX_PANE_WIDTH,
          leftPaneWidth + (deltaX / containerWidth) * 100,
        ),
      );

      setLeftPaneWidth(newLeftWidth);
      setDragStart(e.clientX);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart, leftPaneWidth]);

  /**
   * Memory cleanup effect for Kroki image blob URLs
   */
  useEffect(() => {
    return () => {
      if (diagramUrl) {
        URL.revokeObjectURL(diagramUrl);
      }
    };
  }, [diagramUrl]);

  /**
   * Handle view change
   */
  const handleViewChange = (view: C4ViewType) => {
    setSelectedView(view);
  };

  /**
   * Get button class names for save button
   */
  const getButtonClassName = (): string => {
    const baseClasses =
      'px-3 py-1 rounded text-sm font-medium flex items-center gap-1 transition-colors';
    const enabledClasses =
      'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800';
    const disabledClasses = 'bg-gray-300 text-gray-500 cursor-not-allowed';
    return `${baseClasses} ${
      hasUnsavedChanges && !isSaving ? enabledClasses : disabledClasses
    }`;
  };

  /**
   * Get button title for save button
   */
  const getButtonTitle = (): string => {
    if (isSaving) {
      return 'Saving...';
    }
    if (hasUnsavedChanges) {
      return 'Save diagram (Ctrl+S)';
    }
    return 'No unsaved changes';
  };

  /**
   * Render preview content based on error, loading, and diagram state
   */
  const renderPreviewContent = () => {
    if (error) {
      return (
        <div className="p-6 flex items-center justify-center h-full">
          <div className="text-center">
            <ExclamationTriangleIcon className="w-12 h-12 text-amber-500 mx-auto mb-2" />
            <p className="text-gray-600 text-sm mb-2">
              Error rendering diagram
            </p>
            <p className="text-gray-500 text-xs max-w-md">{error}</p>
          </div>
        </div>
      );
    }

    if (isLoading) {
      return (
        <div className="p-6 flex items-center justify-center h-full">
          <div className="text-center">
            <div className="loading loading-spinner loading-lg text-primary" />
            <p className="mt-4 text-gray-600 text-sm">Rendering diagram...</p>
          </div>
        </div>
      );
    }

    if (diagramUrl) {
      return (
        <div className="p-6 flex items-center justify-center h-full">
          <img
            src={diagramUrl}
            alt="Structurizr Diagram"
            className="max-w-full max-h-full object-contain"
            onError={() => {
              // Image failed to load
            }}
            onLoad={() => {
              // Successfully loaded
            }}
          />
        </div>
      );
    }

    return (
      <div className="p-6 flex items-center justify-center h-full text-gray-400 text-sm">
        Enter Structurizr DSL to render preview
      </div>
    );
  };

  return (
    <div
      ref={containerRef}
      className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
      style={{ height }}
    >
      <div className="flex h-full">
        {/* Left Pane - Monaco DSL Editor */}
        <div
          className="flex flex-col transition-all duration-300"
          style={{ width: `${leftPaneWidth}%` }}
        >
          {/* Editor Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">
              Structurizr DSL
            </h3>
            <div className="flex items-center gap-2">
              <button
                onClick={handleSave}
                disabled={!hasUnsavedChanges || isSaving}
                className={getButtonClassName()}
                title={getButtonTitle()}
              >
                <CloudArrowUpIcon className="w-4 h-4" />
                {isSaving ? 'Saving...' : 'Save'}
              </button>
              {saveError && (
                <span className="text-red-600 text-xs">{saveError}</span>
              )}
            </div>
          </div>

          {/* Monaco Editor Area */}
          <div className="flex-1 overflow-hidden">
            <MonacoEditor
              height="100%"
              language={STRUCTURIZR_LANGUAGE_ID}
              value={dslContent}
              onChange={handleContentChange}
              onMount={handleEditorDidMount}
              theme="structurizr-light"
              options={{
                wordWrap: 'on',
                minimap: { enabled: true, scale: 0.8 },
                scrollBeyondLastLine: false,
                fontSize: 13,
                lineHeight: 1.5,
                fontFamily:
                  'JetBrains Mono, Consolas, "Courier New", monospace',
                automaticLayout: true,
                folding: true,
                lineNumbers: 'on',
                renderWhitespace: 'selection',
                bracketPairColorization: { enabled: true },
                padding: { top: 12, bottom: 12 },
                tabSize: 4,
                insertSpaces: true,
                autoClosingBrackets: 'always',
                autoClosingQuotes: 'always',
                formatOnPaste: false,
                formatOnType: false,
                suggestOnTriggerCharacters: true,
                quickSuggestions: true,
                scrollbar: {
                  verticalScrollbarSize: 10,
                  horizontalScrollbarSize: 10,
                },
              }}
            />
          </div>
        </div>

        {/* Resizer Handle */}
        <button
          onMouseDown={handleMouseDown}
          className="w-1 bg-gray-300 hover:bg-primary cursor-col-resize transition-colors duration-200"
          aria-label="Resize panes"
          type="button"
        />

        {/* Right Pane - Diagram Preview */}
        <div
          className="flex flex-col transition-all duration-300"
          style={{
            width: `${100 - leftPaneWidth}%`,
          }}
        >
          {/* Preview Header with View Selector */}
          <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">Preview</h3>
            <div className="flex items-center gap-3">
              {/* View Selector */}
              <ViewSelector
                selectedView={selectedView}
                onViewChange={handleViewChange}
                availableViews={availableViews}
                disabled={isLoading ?? !dslContent.trim()}
              />
              {isLoading && (
                <div className="flex items-center gap-2 text-primary">
                  <div className="loading loading-spinner loading-xs" />
                  <span className="text-xs font-medium">Rendering...</span>
                </div>
              )}
            </div>
          </div>

          {/* Preview Area */}
          <div className="flex-1 overflow-auto bg-white">
            {renderPreviewContent()}
          </div>
        </div>
      </div>
    </div>
  );
};
